import {
  confirmIndustrialCostProductionImport,
  previewIndustrialCostProductionImport,
} from '../services/industrialCostService';
import type {
  ProductionImportPreview,
  ProductionImportSummary,
  ProductionUploadMode,
  SourceGranularity,
} from '../types/industrialCost';

export type ProductionImportFlowStatus =
  | 'idle'
  | 'loading'
  | 'needs_mapping'
  | 'needs_resolution'
  | 'ready'
  | 'confirming'
  | 'imported'
  | 'imported_with_issues'
  | 'error';

export type ProductionImportFlowConfig = {
  sourceGranularity: SourceGranularity;
  declaredPeriodYear: string;
  declaredPeriodMonth: string;
  uploadMode: ProductionUploadMode;
};

export type ProductionImportFlowState = {
  file: File | null;
  preview: ProductionImportPreview | null;
  mapping: Record<string, string | null>;
  productResolution: Record<string, string>;
  lineResolution: Record<string, string>;
  config: ProductionImportFlowConfig;
  status: ProductionImportFlowStatus;
  error: string | null;
  resultMessage: string | null;
  normalizationSheetName: string | null;
  normalizationHeaderRowIndex: number | null;
  // IND-004: resultado de la confirmación (descartes y fecha de los datos).
  confirmResult?: ProductionImportSummary | null;
};

const DEFAULT_CONFIG: ProductionImportFlowConfig = {
  sourceGranularity: 'month',
  declaredPeriodYear: '2025',
  declaredPeriodMonth: '',
  uploadMode: 'replace_by_period_range',
};

export function createProductionImportFlowState(
  file: File | null = null,
  config?: Partial<ProductionImportFlowConfig>,
): ProductionImportFlowState {
  return {
    file,
    preview: null,
    mapping: {},
    productResolution: {},
    lineResolution: {},
    config: {
      ...DEFAULT_CONFIG,
      ...config,
    },
    status: 'idle',
    error: null,
    resultMessage: null,
    normalizationSheetName: null,
    normalizationHeaderRowIndex: null,
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeError.response?.data?.detail) {
      return maybeError.response.data.detail;
    }

    if (maybeError.message) {
      return maybeError.message;
    }
  }

  return fallback;
}

function toNullableNumber(value: string): number | null {
  const normalized = value.trim();
  if (!normalized) return null;

  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

function isProductionPreviewMappingValid(preview: ProductionImportPreview): boolean {
  return (
    preview.missing_required_fields.length === 0 &&
    preview.coverage_summary.rows_with_product_ref > 0 &&
    preview.coverage_summary.rows_with_any_quantity > 0
  );
}

export function getProductionUnresolvedProductRefs(
  state: ProductionImportFlowState,
) {
  if (!state.preview) return [];

  return state.preview.detected_product_refs.filter((item) => {
    const resolvedProductId =
      state.productResolution[item.source_value] || item.resolved_product_id;
    return !resolvedProductId;
  });
}

export function getProductionUnresolvedLineRefs(
  state: ProductionImportFlowState,
) {
  if (!state.preview) return [];

  return state.preview.detected_line_refs.filter((item) => {
    const resolvedLineId =
      state.lineResolution[item.source_value] ||
      item.resolved_production_line_config_id;

    return !resolvedLineId;
  });
}

export function canContinueProductionImport(
  state: ProductionImportFlowState,
): boolean {
  if (!state.preview) return false;
  return isProductionPreviewMappingValid(state.preview);
}

export function canGoToProductionReview(
  state: ProductionImportFlowState,
): boolean {
  return (
    canContinueProductionImport(state) &&
    getProductionUnresolvedLineRefs(state).length === 0
  );
}

export async function runProductionImportPreview(
  state: ProductionImportFlowState,
  overrides?: {
    file?: File | null;
    mapping?: Record<string, string | null>;
    productResolution?: Record<string, string>;
    lineResolution?: Record<string, string>;
    config?: Partial<ProductionImportFlowConfig>;
  },
): Promise<ProductionImportFlowState> {
  const file = overrides?.file ?? state.file;
  const mapping = overrides?.mapping ?? state.mapping;
  const nextConfig: ProductionImportFlowConfig = {
    ...state.config,
    ...(overrides?.config ?? {}),
  };
  const incomingResolution = overrides?.productResolution ?? state.productResolution;
  const incomingLineResolution =
    overrides?.lineResolution ?? state.lineResolution;

  if (!file) {
    return {
      ...state,
      file: null,
      preview: null,
      status: 'idle',
      error: null,
      resultMessage: null,
    };
  }

  try {
    const preview = await previewIndustrialCostProductionImport(
      file,
      mapping,
      nextConfig.sourceGranularity,
      toNullableNumber(nextConfig.declaredPeriodYear),
      toNullableNumber(nextConfig.declaredPeriodMonth),
      state.normalizationSheetName,
      state.normalizationHeaderRowIndex,
    );

    const nextResolution: Record<string, string> = { ...incomingResolution };
    preview.detected_product_refs.forEach((item) => {
      if (!nextResolution[item.source_value] && item.resolved_product_id) {
        nextResolution[item.source_value] = item.resolved_product_id;
      }
    });

    let status: ProductionImportFlowStatus = 'ready';

    if (!isProductionPreviewMappingValid(preview)) {
      status = 'needs_mapping';
    }

    return {
      ...state,
      file,
      preview,
      mapping: preview.auto_mapping ?? mapping,
      productResolution: nextResolution,
      lineResolution: incomingLineResolution,
      config: nextConfig,
      status,
      error: null,
      resultMessage: null,
    };
  } catch (error) {
    return {
      ...state,
      file,
      preview: null,
      config: nextConfig,
      status: 'error',
      error: getErrorMessage(error, 'No se pudo leer el archivo de producción.'),
      resultMessage: null,
    };
  }
}

export async function applyProductionImportMapping(
  state: ProductionImportFlowState,
  field: string,
  column: string,
): Promise<ProductionImportFlowState> {
  const nextMapping = {
    ...state.mapping,
    [field]: column || null,
  };

  return runProductionImportPreview(state, {
    mapping: nextMapping,
  });
}

export async function applyProductionProductResolution(
  state: ProductionImportFlowState,
  sourceValue: string,
  productId: string,
): Promise<ProductionImportFlowState> {
  const nextResolution = {
    ...state.productResolution,
    [sourceValue]: productId,
  };

  return runProductionImportPreview(state, {
    productResolution: nextResolution,
  });
}

export function applyProductionLineResolution(
  state: ProductionImportFlowState,
  sourceValue: string,
  productionLineConfigId: string,
): ProductionImportFlowState {
  return {
    ...state,
    lineResolution: {
      ...state.lineResolution,
      [sourceValue]: productionLineConfigId,
    },
    error: null,
    resultMessage: null,
  };
}

export async function applyProductionImportConfig(
  state: ProductionImportFlowState,
  config: Partial<ProductionImportFlowConfig>,
): Promise<ProductionImportFlowState> {
  return runProductionImportPreview(state, {
    config,
  });
}

/** IND-053: una carga por rango que trae menos de la mitad de las filas que va
 * a sustituir (y al menos 10 guardadas) se avisa antes de publicar. */
export const PRODUCTION_SHRINK_MIN_EXISTING = 10;

export function productionShrinkWarning(
  preview: ProductionImportPreview | null,
  uploadMode: ProductionUploadMode,
): string | null {
  const existing = preview?.existing_rows_in_range ?? null;
  if (!preview || uploadMode !== 'replace_by_date_range' || existing === null) return null;
  const incoming = preview.coverage_summary.rows_with_any_quantity;
  if (existing < PRODUCTION_SHRINK_MIN_EXISTING || incoming * 2 >= existing) return null;
  const day = (value: string | null) => (value ?? '').split('-').reverse().join('/');
  const range = preview.detected_date_min === preview.detected_date_max
    ? `del ${day(preview.detected_date_min)}`
    : `del ${day(preview.detected_date_min)} al ${day(preview.detected_date_max)}`;
  return `El fichero de producción trae ${incoming} filas y va a sustituir las ${existing} que hay guardadas ${range}. ¿Publicarlo igualmente?`;
}

export async function confirmProductionImport(
  state: ProductionImportFlowState,
): Promise<ProductionImportFlowState> {
  if (!state.file || !state.preview) {
    return state;
  }

  try {
    const result = await confirmIndustrialCostProductionImport(
      state.file,
      state.mapping,
      state.productResolution,
      state.lineResolution,
      state.config.uploadMode,
      state.config.sourceGranularity,
      toNullableNumber(state.config.declaredPeriodYear),
      toNullableNumber(state.config.declaredPeriodMonth),
      state.normalizationSheetName,
      state.normalizationHeaderRowIndex,
    );

    const hasIssues =
      result.rows_skipped_unresolved_product > 0 ||
      result.row_errors.length > 0 ||
      result.persistence_errors.length > 0;

    const firstRowError = result.row_errors[0];
    const firstRowReason = firstRowError
      ? `fila ${firstRowError.fila}: ${firstRowError.mensaje}`
      : null;
    const issueDetails = [
      result.rows_skipped_unresolved_product > 0
        ? `${result.rows_skipped_unresolved_product} sin producto resuelto`
        : null,
      result.row_errors.length > 0
        ? `${result.row_errors.length} fila(s) no válidas${firstRowReason ? ` (${firstRowReason})` : ''}`
        : null,
      result.persistence_errors.length > 0
        ? `${result.persistence_errors.length} errores al guardar`
        : null,
    ].filter((value): value is string => value !== null);

    const lineAliasText =
      result.line_aliases_upserted > 0
        ? ` Correspondencias de línea guardadas: ${result.line_aliases_upserted}.`
        : '';

    return {
      ...state,
      confirmResult: result,
      status: hasIssues ? 'imported_with_issues' : 'imported',
      error: null,
      resultMessage: `Producción: ${result.rows_imported} filas importadas.${issueDetails.length > 0 ? ` Avisos: ${issueDetails.join(' · ')}.` : ''}${lineAliasText}`,
    };
  } catch (error) {
    return {
      ...state,
      status: 'error',
      error: getErrorMessage(
        error,
        'No se pudo confirmar la importación de producción.',
      ),
      resultMessage: null,
    };
  }
}